import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { useModal } from "../../../../hooks/use-modal";
import { useDisable } from "../../../../hooks/use-disable";
import { usePurchaseItemListHeader } from "../../../../hooks/Purchase/use-purchase-item-list-header";
import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { AgGridReact } from "ag-grid-react";

import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import ItemNameCell from "./ItemNameCell";
import SumPreVatAmount from "./SumPreVatAmount";
import LabelInput from "components/UI/LabelInput";
import PreVatAmountCell from "./PreVatAmountCell";
import ButtonLayout from "../../../UI/ButtonLayout";
import Confirmation from "../../../UI/Confirmation";
import WithholdingTaxCell from "./WithholdingTaxCell";
import { CustomizedBox } from "../../../Custom/CustomizedBox";
import CustomizedButton from "../../../Custom/CustomizedButton";
import ControlledSelect from "../../../Controller/ControlledSelect";
import ControlledTextField from "../../../Controller/ControlledTextField";
import ControlledNumberTextField from "../../../Controller/ControlledNumberTextField";
import CheckedItemListModal from "components/UI/Modal/CheckedModal/CheckedItemListModal";
import PurchaseItemTableModal from "components/UI/Modal/CheckedModal/PurchaseItemTableModal";

import { ISelectOption } from "../../../../types/global";
import { IPurchaseItemList } from "../../../../types/Purchase";
import { IPurchaseOrder } from "../../../../types/Purchase/purchaseOrder";

import {
  numberFormatter,
  priceNumberFormatter,
  priceVatTypeFormatter,
  vatTypeFormatter,
} from "../../../../utils/Formatter/Global";
import { IItem } from "types/Inventory/item";
import { PriceVatType } from "generated/purchase";

// import { ITEM_SKU_DETAIL_AGGRID } from "../../../../services/AgGrid/InventoryAggrid";

interface Props {
  documentType?: string;
  referenceItemList?: IPurchaseItemList[];
}

const vatOptions: ISelectOption[] = [
  {
    label: "ไม่มี",
    value: "ไม่มี",
  },
  {
    label: "0 %",
    value: "0 %",
  },
  {
    label: "7 %",
    value: "7 %",
  },
];

const priceVatTypeOptions: ISelectOption[] = [
  {
    label: "ราคารวมภาษี",
    value: PriceVatType.IncludedVat,
  },
  {
    label: "ราคาแยกภาษี",
    value: PriceVatType.ExcludedVat,
  },
];

const PurchaseItemList = ({
  documentType = "purchase_order",
  referenceItemList,
}: Props) => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext<IPurchaseOrder>();
  const [disabled] = useDisable();
  const status = useWatch({ control, name: "aggrid_status" });
  const headers = usePurchaseItemListHeader(documentType, disabled, status);

  const [openDeleteItemConfirmation, setOpenDeleteItemConfirmation] =
    useState<boolean>(false);
  const [deletedIndex, setDeletedIndex] = useState<number | undefined>(
    undefined
  );

  const {
    modal: itemModal,
    openModalHandler: openItemModalHandler,
    closeModalHandler: closeItemModalHandler,
  } = useModal();

  const {
    modal: itemRSModal,
    openModalHandler: openItemRSModalHandler,
    closeModalHandler: closeItemRSModalHandler,
  } = useModal();

  const [itemIds, setItemIds] = useState<string[]>([]);
  const [itemIdsSnapshot, setItemIdsSnapshot] = useState<string[]>([]);

  useEffect(() => {
    if (referenceItemList && referenceItemList.length > 0) {
      const uniqueIdList = referenceItemList.map(
        ({ unique_id }) => unique_id || ""
      );
      setItemIds(uniqueIdList);
      setItemIdsSnapshot(uniqueIdList);
    }
  }, [referenceItemList]);

  const { fields, move, remove, append, replace } = useFieldArray({
    control,
    name: "item_list",
    keyName: "unique_id",
  });

  const watchItemList = watch("item_list");

  const dragEndHandler = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    move(result.source.index, result.destination.index);
  };

  const openDeleteItemConfirmationHandler = (index: number) => {
    setOpenDeleteItemConfirmation(true);
    setDeletedIndex(index);
  };

  const closeDeleteItemConfirmationHandler = () => {
    setOpenDeleteItemConfirmation(false);
    setDeletedIndex(undefined);
  };

  const deleteItemConfirmationAction = () => {
    if (deletedIndex || deletedIndex === 0) {
      remove(deletedIndex);
      setItemIds((prev) => prev.filter((_, index) => index !== deletedIndex));
      setItemIdsSnapshot((prev) =>
        prev.filter((_, index) => index !== deletedIndex)
      );
    }
    closeDeleteItemConfirmationHandler();
  };

  const addReferenceItemsHandler = (itemList: any[]) => {
    if (itemList && itemList.length > 0) {
      let filteredItems = fields.filter((item) =>
        itemIds.includes(item.unique_id)
      );

      // get current Items id to prevent duplication when appending newly selected Items
      const filteredItemsId = filteredItems.map((item) => item.unique_id);

      itemList.forEach((item) => {
        if (!filteredItemsId.includes(item.unique_id)) {
          filteredItems.push(item);
        }
      });

      // sort for proerly render order (and for role assignment when submitting)
      filteredItems = filteredItems.sort((a, b) => {
        return itemIds.indexOf(a.unique_id) - itemIds.indexOf(b.unique_id);
      });

      replace(filteredItems);
    } else if (itemList.length === 0) {
      replace([]);
    }
  };

  const addItemsHandler = () => {
    const selectedItems = gridRef?.current?.api.getSelectedRows();
    if (selectedItems) {
      selectedItems.forEach((item_details: IItem) => {
        let formatItems: IPurchaseItemList = {
          unique_id: uuidv4(),
          item_id: item_details.id || 0,
          item_unique_id: item_details.unique_id || "",
          item_name: item_details.name || "",
          item_desc: item_details.description,
          traceability: item_details.tracability,
          qty: item_details.min_purchase_request_qty ?? 1,
          uom_id: item_details.stock_uom_id,
          uom_name: item_details.stock_uom_name,
          uom: {
            id: item_details.stock_uom_id,
            name: item_details.stock_uom_name,
          },
          uom_conversion_list: item_details.uom_conversion_list,
          remark: "",
          stock_qty: item_details.min_purchase_request_qty ?? 1,
          discount: 0,
          pre_vat_amount: 0,
          price_per_unit: item_details.purchase_price ?? 0,
          withholding_tax_type: "ยังไม่ระบุ",
          withholding_tax_value: 0,
          vat_percentage: item_details.purchase_vat_type || "ไม่มี",
          // item_sku_qty: item_details.item_sku_qty_list,
          item_img_url: item_details.img_url_list.map((img) => img.img_url),
          barcode: item_details.barcode || "",
        };

        append(formatItems);
      });
    }
    closeItemModalHandler();
  };

  const uomOptions = (field: IPurchaseItemList) => {
    const options = (
      field.uom_conversion_list
        ? field.uom_conversion_list.length > 0
          ? [
              {
                label: field.uom?.name || "",
                value: field.uom_id || "",
              },
              ...field.uom_conversion_list.map((uom) => ({
                label: uom.target_uom_name,
                value: uom.target_uom_id,
              })),
            ]
          : [
              {
                label: field.uom?.name || "",
                value: field.uom_id || "",
              },
            ]
        : []
    ) as ISelectOption[];
    return options;
  };

  const rsPage = documentType === "purchase_return";

  const finishedPOStatus = [
    "partially_imported",
    "fully_imported",
    "fully_ordered",
  ];

  const finishedRSStatus = ["finished"];

  const checkIsExceed = (index: number) => {
    const watchQty = watchItemList[index].qty;
    const watchPOQty = watchItemList[index].po_qty || 0;
    return rsPage && watchQty > watchPOQty;
  };

  return (
    <CustomizedBox
      sx={{ breakAfter: "auto" }}
      margin="1rem 0 0 0"
      maxWidth={1650}
    >
      <Typography fontWeight={600}>สินค้า</Typography>
      <Grid container mt={0} mb={2} spacing={2}>
        <Grid item sm={12} md={4}>
          {disabled ? (
            <LabelInput
              label={t("purchase.price_vat_type")}
              value={priceVatTypeFormatter(getValues("price_vat_type"))}
            />
          ) : (
            <ControlledSelect
              control={control}
              name="price_vat_type"
              label={t("sales.price_vat_type")}
              options={priceVatTypeOptions}
              error={Boolean(errors.price_vat_type)}
              helperText={errors?.price_vat_type?.message?.toString()}
              disabled={disabled || rsPage}
              required
            />
          )}
        </Grid>
      </Grid>
      <TableContainer>
        <Table
          sx={{ minWidth: 650, overflow: "scroll" }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell
                  align="center"
                  key={index}
                  sx={{
                    px: 1,
                    py: 1.5,
                    backgroundColor: "secondary.main",
                  }}
                  width={header.width}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      width: header.width,
                      justifyContent: header.align || "center",
                    }}
                  >
                    <Typography fontSize={14} fontWeight={600}>
                      {header.thaiLabel}
                    </Typography>
                    {header.required && (
                      <Typography
                        fontSize={14}
                        color={"error.main"}
                        fontWeight={600}
                      >
                        *
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <DragDropContext onDragEnd={dragEndHandler}>
            <Droppable droppableId="droppable" isDropDisabled={disabled}>
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {fields.map((field, index) => (
                    <Draggable
                      key={field.unique_id}
                      draggableId={"item-" + field.unique_id}
                      index={index}
                      isDragDisabled={disabled}
                    >
                      {(provided) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            ...provided.draggableProps.style,
                            breakInside: "avoid",
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[0]?.width || 0
                                : 0) + 16
                            }
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {!disabled && (
                                <MenuIcon
                                  fontSize="small"
                                  sx={{ color: "#BDBDBD" }}
                                />
                              )}
                              <Typography fontSize={14}>{index + 1}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[1]?.width || 0
                                : 0) + 16
                            }
                          >
                            <ItemNameCell
                              control={control}
                              errors={errors}
                              field={field}
                              index={index}
                              disabled={disabled}
                              documentType={documentType}
                            />
                          </TableCell>
                          {rsPage && (
                            <TableCell
                              align="right"
                              sx={{
                                px: 1,
                                py: 1,
                              }}
                              width={
                                (headers && headers.length > 0
                                  ? headers[2]?.width || 0
                                  : 0) + 16
                              }
                            >
                              {disabled ? (
                                <Typography fontSize={14}>
                                  {numberFormatter(field.po_qty || 0)}
                                </Typography>
                              ) : (
                                <ControlledNumberTextField
                                  control={control}
                                  name={`item_list.${index}.po_qty`}
                                  error={Boolean(
                                    errors?.item_list &&
                                      errors?.item_list[index] &&
                                      errors?.item_list[index]?.qty
                                  )}
                                  disabled={true}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell
                            align="right"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[2]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {numberFormatter(field.qty)}
                              </Typography>
                            ) : (
                              <ControlledNumberTextField
                                control={control}
                                name={`item_list.${index}.qty`}
                                error={
                                  Boolean(
                                    errors?.item_list &&
                                      errors?.item_list[index] &&
                                      errors?.item_list[index]?.qty
                                  ) || Boolean(checkIsExceed(index))
                                }
                                helperText={
                                  Boolean(checkIsExceed(index)) &&
                                  t("purchase.return.exceed_po_qty")
                                }
                                sx={{
                                  marginTop: Boolean(checkIsExceed(index))
                                    ? 2.5
                                    : undefined,
                                }}
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    style: { textAlign: "right" },
                                  },
                                }}
                                onChange={(value) => {
                                  const parseValue = value || 0;
                                  const currentUomId = getValues(
                                    `item_list.${index}.uom_id`
                                  );
                                  if (currentUomId) {
                                    if (field.uom_conversion_list) {
                                      if (currentUomId === field.uom_id) {
                                        setValue(
                                          `item_list.${index}.stock_qty`,
                                          parseValue
                                        );
                                      } else {
                                        if (
                                          field.uom_conversion_list &&
                                          field.uom_conversion_list.length > 0
                                        ) {
                                          const conversionUom =
                                            field.uom_conversion_list.find(
                                              (conversion) =>
                                                conversion.target_uom_id ===
                                                currentUomId
                                            );
                                          if (conversionUom) {
                                            const targetUomQty =
                                              conversionUom.base_uom_rate /
                                              conversionUom.target_uom_rate;
                                            setValue(
                                              `item_list.${index}.stock_qty`,
                                              parseValue * targetUomQty
                                            );
                                          }
                                        }
                                      }
                                    }
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[3]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {field.uom_name}
                              </Typography>
                            ) : (
                              <ControlledSelect
                                control={control}
                                name={`item_list.${index}.uom_id`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.uom_id
                                )}
                                options={uomOptions(field)}
                                disabled={rsPage}
                                onChange={(e: any) => {
                                  const uom_name = uomOptions(field).find(
                                    (uom) => uom.value === e.target.value
                                  )?.label;
                                  setValue(
                                    `item_list.${index}.uom_name`,
                                    uom_name
                                  );
                                  const currentQty = getValues(
                                    `item_list.${index}.qty`
                                  );
                                  if (field.uom_conversion_list) {
                                    if (e.target.value === field.uom.id) {
                                      setValue(
                                        `item_list.${index}.stock_qty`,
                                        currentQty
                                      );
                                    } else {
                                      if (
                                        field.uom_conversion_list &&
                                        field.uom_conversion_list.length > 0
                                      ) {
                                        const conversionUom =
                                          field.uom_conversion_list.find(
                                            (conversion) =>
                                              conversion.target_uom_id ===
                                              e.target.value
                                          );
                                        if (conversionUom) {
                                          const targetUomQty =
                                            conversionUom.base_uom_rate /
                                            conversionUom.target_uom_rate;
                                          setValue(
                                            `item_list.${index}.stock_qty`,
                                            currentQty * targetUomQty
                                          );
                                        }
                                      }
                                    }
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[4]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {priceNumberFormatter(field.price_per_unit)}
                              </Typography>
                            ) : (
                              <ControlledNumberTextField
                                control={control}
                                name={`item_list.${index}.price_per_unit`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.price_per_unit
                                )}
                                decimalScale={4}
                                disabled={rsPage}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[5]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {priceNumberFormatter(field.discount)}
                              </Typography>
                            ) : (
                              <ControlledNumberTextField
                                control={control}
                                name={`item_list.${index}.discount`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.discount
                                )}
                                decimalScale={4}
                                disabled={rsPage}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[6]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {vatTypeFormatter(field.vat_percentage)}
                              </Typography>
                            ) : (
                              <ControlledSelect
                                control={control}
                                name={`item_list.${index}.vat_percentage`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.vat_percentage
                                )}
                                options={vatOptions}
                                disabled={rsPage}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[7]?.width || 0
                                : 0) + 16
                            }
                          >
                            <PreVatAmountCell
                              control={control}
                              index={index}
                              field={field}
                              disabled={disabled || rsPage}
                              setValue={setValue}
                            />
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[8]?.width || 0
                                : 0) + 16
                            }
                          >
                            <WithholdingTaxCell
                              control={control}
                              errors={errors}
                              index={index}
                              documentType={documentType}
                              viewMode={disabled}
                              disabled={rsPage}
                              setValue={setValue}
                              field={field}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[9]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <>
                                <Typography fontSize={14}>
                                  {numberFormatter(field.stock_qty)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="gray.light"
                                >
                                  {field.uom.name}
                                </Typography>
                              </>
                            ) : (
                              <ControlledTextField
                                sx={{ mt: 3 }}
                                type="number"
                                control={control}
                                name={`item_list.${index}.stock_qty`}
                                InputProps={{
                                  inputProps: {
                                    style: { textAlign: "center" },
                                  },
                                }}
                                helperText={field.uom?.name}
                                uomHelperText={true}
                                viewMode={true}
                              />
                            )}
                          </TableCell>
                          {finishedPOStatus.includes(status || "") && (
                            <TableCell
                              align="right"
                              sx={{
                                px: 1,
                                py: 1,
                              }}
                              width={
                                (headers && headers.length > 0
                                  ? headers[9]?.width || 0
                                  : 0) + 16
                              }
                            >
                              <>
                                <Typography fontSize={14}>
                                  {numberFormatter(field.po_qty || 0)}
                                </Typography>
                              </>
                            </TableCell>
                          )}
                          {finishedRSStatus.includes(status || "") && (
                            <TableCell
                              align="right"
                              sx={{
                                px: 1,
                                py: 1,
                              }}
                              width={
                                (headers && headers.length > 0
                                  ? headers[9]?.width || 0
                                  : 0) + 16
                              }
                            >
                              <>
                                <Typography fontSize={14}>
                                  {numberFormatter(field.rs_qty || 0)}
                                </Typography>
                              </>
                            </TableCell>
                          )}
                          <TableCell
                            align="left"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            width={
                              (headers && headers.length > 0
                                ? headers[10]?.width || 0
                                : 0) + 16
                            }
                          >
                            {disabled ? (
                              <Typography fontSize={14}>
                                {field.remark}
                              </Typography>
                            ) : (
                              <ControlledTextField
                                control={control}
                                name={`item_list.${index}.remark`}
                                error={Boolean(
                                  errors?.item_list &&
                                    errors?.item_list[index] &&
                                    errors?.item_list[index]?.remark
                                )}
                                placeholder={t("sentence.remark")}
                              />
                            )}
                          </TableCell>
                          {!disabled && (
                            <TableCell
                              align="center"
                              sx={{
                                px: 1,
                                py: 1,
                              }}
                              width={
                                (headers && headers.length > 0
                                  ? headers[11]?.width || 0
                                  : 0) + 16
                              }
                            >
                              <IconButton
                                aria-label="delete"
                                size="small"
                                sx={{ ml: 1, color: "rgba(0, 0, 0, 0.54)" }}
                                onClick={() =>
                                  openDeleteItemConfirmationHandler(index)
                                }
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      </TableContainer>
      {!disabled && (
        <ButtonLayout sx={{ marginTop: 2 }}>
          <CustomizedButton
            title={rsPage ? t("button.select_item") : t("button.add_item")}
            variant="outlined"
            onClick={rsPage ? openItemRSModalHandler : openItemModalHandler}
            startAddIcon
          />
        </ButtonLayout>
      )}
      <SumPreVatAmount control={control} />
      <Confirmation
        title="ยืนยันการลบสินค้า"
        open={openDeleteItemConfirmation}
        handleClose={closeDeleteItemConfirmationHandler}
        action={deleteItemConfirmationAction}
      />
      <PurchaseItemTableModal
        ref={gridRef}
        itemModal={itemModal}
        closeItemModalHandler={closeItemModalHandler}
        addItemsHandler={addItemsHandler}
      />
      <CheckedItemListModal
        showSelectItem={itemRSModal}
        closeItemTable={closeItemRSModalHandler}
        finishItemsSelect={addReferenceItemsHandler}
        rowData={referenceItemList}
        rowSelection="multiple"
        itemIds={itemIds}
        setItemIds={setItemIds}
        itemIdsSnapshot={itemIdsSnapshot}
        setItemIdsSnapshot={setItemIdsSnapshot}
        documentType="purchase_return"
      />
    </CustomizedBox>
  );
};

export default PurchaseItemList;
